import Papa from 'papaparse';
import {
    validateCSVHeaders,
    validateStudentRow,
    validateInventoryRow,
} from './csvValidationUtils';
import { prepareStudent } from './studentPreparationUtils';
import { prepareInventoryItem } from './inventoryPreparationUtils';

// === STUDENT CSV IMPORT ===
export const handleCSVUpload = async (event, loadStudents, showAlert, checkStudentIdExists, addStudent) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
            const students = results.data;
            const actualHeaders = results.meta.fields;
            const { missingHeaders, extraHeaders } = validateCSVHeaders(actualHeaders);

            if (missingHeaders.length > 0 || extraHeaders.length > 0) {
                showAlert(
                    `CSV Import Failed: Incorrect headers.\n\n` +
                    `Missing fields: ${missingHeaders.join(", ")}\n` +
                    `Unexpected fields: ${extraHeaders.join(", ")}`
                );
                return;
            }

            let addedCount = 0;
            let skippedStudents = [];

            for (const student of students) {
                const preparedStudent = prepareStudent(student);
                const rowErrors = validateStudentRow(preparedStudent);

                if (rowErrors.length > 0) {
                    skippedStudents.push({
                        id: preparedStudent.studentId,
                        reason: rowErrors.join("; ")
                    });
                    continue;
                }

                try {
                    const exists = await checkStudentIdExists(preparedStudent.studentId);
                    if (!exists) {
                        const success = await addStudent(preparedStudent);
                        if (success) {
                            addedCount++;
                        } else {
                            skippedStudents.push({ id: preparedStudent.studentId, reason: "Server error while adding. Check server logs!!" });
                        }
                    } else {
                        skippedStudents.push({ id: preparedStudent.studentId, reason: "Student ID already exists" });
                    }
                } catch (error) {
                    console.error('Error adding student:', preparedStudent, error);
                    skippedStudents.push({ id: preparedStudent.studentId, reason: "Unexpected error" });
                }
            }

            loadStudents();

            let message = `CSV import completed!\n\nStudents added: ${addedCount}\nStudents skipped: ${skippedStudents.length}`;
            if (skippedStudents.length > 0) {
                message += `\n\nSkipped Students:\n`;
                skippedStudents.forEach(student => {
                    message += `- Student ID ${student.id}: ${student.reason}\n`;
                });
            }

            showAlert(message);
        },
        error: (err) => {
            console.error("Error parsing CSV:", err);
            showAlert('Failed to parse CSV file.');
        }
    });
};

// === INVENTORY CSV IMPORT ===
export const handleInventoryCSVUpload = async (event, loadInventory, showAlert, addAsset, getOrCreateLocation, getOrCreateStudentClass) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
            const inventoryItems = results.data;
            const actualHeaders = results.meta.fields;
            const { missingHeaders, extraHeaders } = validateCSVHeaders(actualHeaders, 'inventory');

            if (missingHeaders.length > 0 || extraHeaders.length > 0) {
                showAlert(
                    `CSV Import Failed: Incorrect headers.\n\n` +
                    (missingHeaders.length > 0 ? `Missing fields: ${missingHeaders.join(", ")}\n` : "") +
                    (extraHeaders.length > 0 ? `Unexpected fields: ${extraHeaders.join(", ")}` : "")
                );
                return;
            }

            let addedCount = 0;
            let skippedItems = [];
            const processedLocations = new Set(); // Track locations we've already processed
            const processedStudentClasses = new Set(); // Track student classes we've already processed

            for (const row of inventoryItems) {
                const rowErrors = validateInventoryRow(row);

                if (rowErrors.length > 0) {
                    skippedItems.push({
                        id: row.serialNo || 'UNKNOWN',
                        reason: rowErrors.join("; ")
                    });
                    continue;
                }

                // Auto-add location if it exists and hasn't been processed yet
                if (row.location && row.location.trim() && getOrCreateLocation) {
                    const locationName = row.location.trim();
                    if (!processedLocations.has(locationName)) {
                        try {
                            await getOrCreateLocation(locationName);
                            processedLocations.add(locationName);
                        } catch (err) {
                            console.error("Failed to add location from CSV:", locationName, err);
                        }
                    }
                }

                // Auto-add student class if it exists and hasn't been processed yet
                if (row.allowedClasses && row.allowedClasses.trim() && getOrCreateStudentClass) {
                    const studentClassName = row.allowedClasses.trim();
                    if (!processedStudentClasses.has(studentClassName)) {
                        try {
                            await getOrCreateStudentClass(studentClassName);
                            processedStudentClasses.add(studentClassName);
                        } catch (err) {
                            console.error("Failed to add student class from CSV:", studentClassName, err);
                        }
                    }
                }

                const preparedItem = prepareInventoryItem(row);

                try {
                    const success = await addAsset(preparedItem);
                    if (success) {
                        addedCount++;
                    } else {
                        skippedItems.push({
                            id: row.serialNo || 'UNKNOWN',
                            reason: "Server error while adding asset."
                        });
                    }
                } catch (err) {
                    console.error("Error adding inventory item:", preparedItem, err);
                    skippedItems.push({
                        id: row.serialNo || 'UNKNOWN',
                        reason: "Unexpected error during upload."
                    });
                }
            }

            loadInventory();

            let message = `CSV import completed!\n\nItems added: ${addedCount}\nItems skipped: ${skippedItems.length}`;
            if (skippedItems.length > 0) {
                message += `\n\nSkipped Items:\n`;
                skippedItems.forEach(item => {
                    message += `- Serial ${item.id}: ${item.reason}\n`;
                });
            }

            showAlert(message);
        },
        error: (err) => {
            console.error("Error parsing CSV:", err);
            showAlert('Failed to parse CSV file.');
        }
    });
};
